import React from 'react';
import { fmtDate as _fmtDate, fmtDateTime as _fmtDateTime } from '@/lib/dates';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ShieldCheck } from 'lucide-react';

// Shared three-role request chain. Never combines the roles and shows graceful
// blanks for legacy records. Used by ServiceRequestDetail, WorkOrderDetail and
// the client-facing history so the chain wording never drifts.

const fmtDate = (d) => _fmtDate(d) || '—';
const fmtDateTime = (d) => _fmtDateTime(d) || '—';

const RoleCell = ({ index, label, name, lines = [], cellClass, testId }) => (
  <div className={`rounded-lg p-2.5 ${cellClass}`}>
    <div className="text-slate-400 text-[10px] uppercase tracking-wide">{index} · {label}</div>
    <div className="text-slate-800 font-semibold" data-testid={testId}>{name || '—'}</div>
    {lines.filter(Boolean).map((l, i) => <div key={i} className="text-[11px] text-slate-500">{l}</div>)}
  </div>
);

const RequestChainCard = ({
  record = {},
  variant = 'panel',       // 'panel' (tinted box) | 'card' (shadcn Card)
  prefix = 'sr',           // data-testid prefix (sr | wo)
  showAuthorizedBadge = false,
  showReceived = false,
  enteredByFallback = null,
}) => {
  const cellClass = variant === 'card'
    ? 'border border-slate-100 bg-slate-50/70'
    : 'bg-white/70 border border-slate-100';

  const enteredBy = record.entered_by_name
    || (record.entered_by_tfs ? 'TFS' : enteredByFallback)
    || '—';

  const intakeLine = `${record.intake_source || '—'}${record.source_system ? ` · ${record.source_system}` : ''}`;

  const receivedLine = showReceived
    ? `Received ${fmtDate(record.received_by_tfs_date) !== '—' ? fmtDate(record.received_by_tfs_date) : fmtDateTime(record.submitted_at)}`
    : null;

  const grid = (
    <div className={`grid sm:grid-cols-3 ${variant === 'card' ? 'gap-3' : 'mt-2 gap-x-6 gap-y-3'} text-sm`}>
      <RoleCell
        index="1" label="Original Requester" cellClass={cellClass}
        name={record.requester_contact_name || record.requested_by_name}
        lines={[record.requester_contact_title, record.requester_contact_email]}
        testId={`${prefix}-chain-requester`}
      />
      <RoleCell
        index="2" label="Client Authorizer / Forwarder" cellClass={cellClass}
        name={record.client_authorized_by}
        lines={[record.client_authorized_by_role, record.authorized_by_email]}
        testId={`${prefix}-chain-authorizer`}
      />
      <RoleCell
        index="3" label="Entered Into ROUT By" cellClass={cellClass}
        name={enteredBy}
        lines={[intakeLine, receivedLine]}
        testId={`${prefix}-chain-entered-by`}
      />
    </div>
  );

  if (variant === 'card') {
    return (
      <Card className="border-slate-200 shadow-sm rounded-2xl" data-testid={`${prefix}-request-chain`}>
        <CardHeader><CardTitle className="text-base text-[#0E2A47]">Request Chain</CardTitle></CardHeader>
        <CardContent>{grid}</CardContent>
      </Card>
    );
  }

  return (
    <div className="rounded-xl border border-[#0E2A47]/15 bg-[#0E2A47]/[0.03] p-4" data-testid={`${prefix}-request-chain`}>
      {showAuthorizedBadge && record.entered_by_tfs && record.client_authorized && (
        <div className="inline-flex items-center gap-1.5 rounded-full bg-[#0E2A47] px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
          <ShieldCheck size={13} /> Client Authorized — Entered Into ROUT By TFS
        </div>
      )}
      <div className="mt-3 text-[11px] font-bold uppercase tracking-wide text-[#0E2A47]">Request Chain</div>
      {grid}
    </div>
  );
};

export default RequestChainCard;
